/**
 * Capability #20 — test data ↔ impact / coverage intelligence (compose-on-read).
 * Advisory only — never mutates test data or claims safe-skip.
 */
import { z } from 'zod';
import { extractTestDataReferences, parseTestDataReference } from './test-data-refs';

export const TEST_DATA_IMPACT_EVIDENCE_BANDS = [
  'available',
  'limited',
  'insufficient_evidence',
  'unavailable',
] as const;
export type TestDataImpactEvidenceBand = (typeof TEST_DATA_IMPACT_EVIDENCE_BANDS)[number];

export const TEST_DATA_IMPACT_RELATION_KINDS = [
  'linked_to_impacted_scenario',
  'linked_to_impacted_automation',
  'contributing_to_coverage',
  'no_demonstrated_impact',
  'missing_relationship_evidence',
] as const;
export type TestDataImpactRelationKind = (typeof TEST_DATA_IMPACT_RELATION_KINDS)[number];

export const TestDataImpactGovernanceSchema = z.object({
  autoApply: z.literal(false),
  autoMutateTestData: z.literal(false),
  claimsSafeSkip: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
  advisoryOnly: z.literal(true),
  claimsCoverageCausedByTestData: z.literal(false),
  claimsTimeSaved: z.literal(false),
});
export type TestDataImpactGovernance = z.infer<typeof TestDataImpactGovernanceSchema>;

export const TEST_DATA_IMPACT_GOVERNANCE: TestDataImpactGovernance = {
  autoApply: false,
  autoMutateTestData: false,
  claimsSafeSkip: false,
  recommendationsAreNotTruth: true,
  advisoryOnly: true,
  claimsCoverageCausedByTestData: false,
  claimsTimeSaved: false,
};

export const TestDataImpactFindingSchema = z.object({
  id: z.string().max(120),
  relationKind: z.enum(TEST_DATA_IMPACT_RELATION_KINDS),
  evidenceBand: z.enum(TEST_DATA_IMPACT_EVIDENCE_BANDS),
  reason: z.string().max(500),
  testDataId: z.string().uuid().nullable(),
  testDataKey: z.string().max(120),
  testDataName: z.string().max(300).nullable().optional(),
  scenarioId: z.string().uuid().nullable().optional(),
  scenarioTitle: z.string().max(300).nullable().optional(),
  flowId: z.string().uuid().nullable().optional(),
  flowName: z.string().max(300).nullable().optional(),
  evidenceUsed: z.array(z.string().max(200)).max(12).default([]),
  evidenceMissing: z.array(z.string().max(200)).max(8).default([]),
  deepLink: z.string().max(500).optional(),
});
export type TestDataImpactFinding = z.infer<typeof TestDataImpactFindingSchema>;

export const TestDataImpactIntelligenceSchema = z.object({
  attribution: z.literal('test_data_impact_compose'),
  projectId: z.string().uuid(),
  orgId: z.string().uuid().nullable().optional(),
  evidenceBand: z.enum(TEST_DATA_IMPACT_EVIDENCE_BANDS),
  summary: z.string().max(600),
  why: z.array(z.string().max(300)).max(8).default([]),
  governance: TestDataImpactGovernanceSchema,
  counts: z.object({
    catalogItems: z.number().int().nonnegative(),
    linkedToImpactedScenario: z.number().int().nonnegative(),
    linkedToImpactedAutomation: z.number().int().nonnegative(),
    contributingToCoverage: z.number().int().nonnegative(),
    noDemonstratedImpact: z.number().int().nonnegative(),
    missingRelationshipEvidence: z.number().int().nonnegative(),
    impactedScenariosScanned: z.number().int().nonnegative(),
    impactedFlowsScanned: z.number().int().nonnegative(),
  }),
  findings: z.array(TestDataImpactFindingSchema).max(40),
  limits: z.object({
    maxCatalogItems: z.number().int().positive(),
    maxImpactedEntities: z.number().int().positive(),
    maxFindings: z.number().int().positive(),
    comparisonStrategy: z.string().max(160),
  }),
});
export type TestDataImpactIntelligence = z.infer<typeof TestDataImpactIntelligenceSchema>;

/** Collect unique data-library keys referenced as `${Key.col}` in free text. */
export function extractDataLibraryKeysFromText(text: string): string[] {
  const keys = new Set<string>();
  for (const token of extractTestDataReferences(text)) {
    const parsed = parseTestDataReference(token);
    if (parsed?.kind === 'data' && parsed.dataKey) keys.add(parsed.dataKey);
  }
  return [...keys].sort();
}

/** Walk JSON/text blobs for `${Key.col}` references (bounded stringify). */
export function extractDataLibraryKeysFromUnknown(value: unknown, maxChars = 200_000): string[] {
  if (value == null) return [];
  if (typeof value === 'string') return extractDataLibraryKeysFromText(value);
  try {
    const text = JSON.stringify(value);
    if (!text || text.length > maxChars) {
      return extractDataLibraryKeysFromText(text.slice(0, maxChars));
    }
    return extractDataLibraryKeysFromText(text);
  } catch {
    return [];
  }
}
